import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { currentBranding } from '../../../config/branding';
import {
  connectorTypes,
  desiredPowerOutputUnits,
  deviceModelSeries,
  maxPowerOutputUnits,
  branding,
} from '../../../config/enum';
import { DeviceDetailsEdit } from '../../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllStationsList } from '../../../redux/slice/ApprovedStationManagementSlice';
import {
  fetchAllDevices,
  fetchChargePointModels,
  fetchChargePointVendors,
} from '../../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomInputSelectBox from '../../../shared-components/custom-input-select-box/CustomInputSelectBox';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { floatingNumberValidation, stringValidation } from '../../../shared-functions/Validation';
import deviceApi from '../../../utils/api/device';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * EditDevice component handles the creation of new devices in the device management system.
 * It provides a form interface for users to input device details such as name, ID, and power specifications.
 * The component includes form validation, submission handling, and navigation controls.
 * @returns {JSX.Element} A form interface for adding new devices
 */
function EditDevice() {
  const { t } = useTranslation('deviceManagement');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { allStations, loading } = useAppSelector((state) => state.approvedStation);
  const { page, limit, devices, chargePointVendors, chargePointModels } = useAppSelector(
    (states) => states.device,
  );

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  const oldDevice = devices.find((device) => device.id === id);

  const [deviceDetails, setDeviceDetails] = useState<DeviceDetailsEdit>({
    chargingStation: oldDevice?.chargingStationId || '',
    chargePointModel: oldDevice?.chargePointModel || '',
    chargingPointSn: oldDevice?.chargingPointSn || '',
    chargingPointName: oldDevice?.chargingPointName || '',
    connectors: oldDevice?.connectors || [{ connectorId: 1, connectorType: [] }],
    maximumOutputPower: oldDevice?.maximumOutputPower || 0,
    maximumOutputPowerUnit: oldDevice?.maximumOutputPowerUnit || 'kW',
    powerUnit: oldDevice?.chargeRate?.unit || 'W',
    desiredPowerOutput:
      oldDevice?.chargeRate?.unit === 'W'
        ? parseFloat(oldDevice?.chargeRate?.value?.toString() || '0') / 1000
        : oldDevice?.chargeRate?.value || 0,
    isJuiceBoxPncEnabled: oldDevice?.isJuiceBoxPncEnabled || false,
    modelSeries: oldDevice?.modelSeries || '',
    isInVLBC: oldDevice?.isInVLBC ?? false,
    isDeviceOffline: oldDevice?.isDeviceOffline ?? false,
    useTxDefaultProfile: oldDevice?.useTxDefaultProfile ?? false,
    chargePointVendor: oldDevice?.chargePointVendor || '',
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  /**
   * Fetch the all station data function
   */
  const fetchData = () => {
    if (!loading) {
      dispatch(fetchAllStationsList());
    }

    // Fetch charge point vendors, if chargePointVendors list is empty
    if (!chargePointVendors.length) {
      dispatch(fetchChargePointVendors());
    }
  };

  /**
   * Updates the device details state when form fields change.
   * @param {string} name - The name of the field to update
   * @param {string | number} changeValue - The new value for the field
   */
  const handleDeviceDetailChange = (
    name: string,
    changeValue: string | number | boolean | string[],
  ) => {
    setDeviceDetails({
      ...deviceDetails,
      [name]: changeValue,
    });
  };

  /**
   * Handles individual connector type changes.
   * @param {number} connectorId - The ID of the connector to update
   * @param {string[]} selectedTypes - The new selected connector types
   */
  const handleConnectorTypeChange = (connectorId: number, selectedTypes: string[]) => {
    const updatedConnectors = deviceDetails.connectors.map((connector) =>
      connector.connectorId === connectorId
        ? { ...connector, connectorType: selectedTypes }
        : connector,
    );
    setDeviceDetails({
      ...deviceDetails,
      connectors: updatedConnectors,
    });
  };

  /**
   * Resets the form state and navigates back to the device management page.
   * This is called after successful submission or when user confirms cancellation.
   */
  const handleNavigateBack = () => {
    setDeviceDetails({
      chargingStation: '',
      chargingPointName: '',
      chargingPointSn: '',
      chargePointModel: '',
      connectors: [{ connectorId: 1, connectorType: [] }],
      maximumOutputPower: 0,
      maximumOutputPowerUnit: 'kW',
      desiredPowerOutput: 0,
      powerUnit: 'W',
      isJuiceBoxPncEnabled: false,
      modelSeries: '',
      isInVLBC: false,
      isDeviceOffline: false,
      useTxDefaultProfile: false,
      chargePointVendor: '',
    });
    setModalBoxStatus('close');

    navigate('../');
  };

  /**
   * Displays a warning modal when user attempts to cancel form submission.
   * Gives users the option to confirm cancellation or continue editing.
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title={t('cancelTitle')}
          description={t('cancelDescription')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('ok')}
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Handles the submission of device details.
   * Validates required fields, creates a FormData object with device details,
   * and makes an API call to add the station. Shows success notification on completion.
   */
  const handleSubmitDeviceDetails = () => {
    const isAllConnectorsValid =
      deviceDetails.connectors.length > 0 &&
      deviceDetails.connectors.every((connector) => connector.connectorType.length > 0);

    if (
      id &&
      deviceDetails.chargingStation &&
      stringValidation(1, 50, deviceDetails.chargePointModel) &&
      isAllConnectorsValid &&
      ((deviceDetails.maximumOutputPower && deviceDetails.maximumOutputPower > 0) ||
        deviceDetails.maximumOutputPower < 999)
    ) {
      setFormStatus({
        showFormErrors: false,
        buttonLoader: true,
      });
      const request: any = {
        chargingPointName: deviceDetails.chargingPointName,
        chargingPointSn: deviceDetails.chargingPointSn,
        chargingStation: deviceDetails.chargingStation,
        connectors: deviceDetails.connectors,
        maximumOutputPower: deviceDetails.maximumOutputPower,
        maximumOutputPowerUnit: deviceDetails.maximumOutputPowerUnit,
        chargeRate: {
          unit: deviceDetails.powerUnit,
          value: deviceDetails.desiredPowerOutput,
        },
        isJuiceBoxPncEnabled: deviceDetails.isJuiceBoxPncEnabled,
        useTxDefaultProfile: deviceDetails.useTxDefaultProfile,
        modelSeries: deviceDetails.modelSeries,
        chargePointVendor: deviceDetails?.chargePointVendor,
        chargePointModel: deviceDetails.chargePointModel,
      };
      if (deviceDetails.powerUnit === 'W') {
        request.chargeRate.value = String(deviceDetails.desiredPowerOutput * 1000);
      }
      if (deviceDetails.maximumOutputPowerUnit === 'W') {
        request.maximumOutputPower = String(deviceDetails.maximumOutputPower * 1000);
      }
      deviceApi
        .editDevice(request, id)
        .then(() => {
          dispatch(fetchAllDevices({ page, limit }));
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('deviceUpdatedSuccess')}
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: true,
            buttonLoader: false,
          });
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    } else {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
    }
  };

  let warningMessage = '';

  if (branding.ENERGIC === currentBranding) {
    if (deviceDetails.isInVLBC || deviceDetails.isDeviceOffline) {
      warningMessage =
        'Device communication updates are not allowed while offline or linked to a load balancer';
    }
  } else if (branding.VOLTIE === currentBranding) {
    if (deviceDetails.isInVLBC) {
      warningMessage =
        'Device communication updates are not allowed while linked to a load balancer';
    }
  }

  /**
   * Function to fetch charge point models by chargePointVendor
   */
  const fetchChargePointModel = () => {
    dispatch(fetchChargePointModels(deviceDetails?.chargePointVendor));
  };

  useEffect(() => {
    if (deviceDetails?.chargePointVendor) {
      fetchChargePointModel();
    }
  }, [deviceDetails?.chargePointVendor]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="add-device-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="add-device-header">
        <PageTitle title={t('editDevice')} />
      </div>
      <div className="add-device-container">
        <div className="add-device-from-wrap">
          <div className="add-device-from">
            <SelectFiled
              inputLabel={t('stationName')}
              name="chargingStation"
              placeholder={t('selectStation')}
              options={allStations}
              selectedValue={deviceDetails.chargingStation}
              valueRequired
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.chargingStation && formStatus.showFormErrors}
              errorMessage={t('stationNameRequired')}
            />
            <CustomInput
              inputLabel={t('deviceName')}
              type="text"
              placeholder={t('enterDeviceName')}
              name="chargingPointName"
              value={deviceDetails.chargingPointName}
              onChangeValue={handleDeviceDetailChange}
            />
            <CustomInput
              inputLabel={t('modelName')}
              type="text"
              placeholder={t('enterModelName')}
              name="chargePointModel"
              valueRequired
              value={deviceDetails.chargePointModel}
              onChangeValue={handleDeviceDetailChange}
              error={
                !stringValidation(1, 50, deviceDetails.chargePointModel) &&
                formStatus.showFormErrors
              }
              readOnly
              errorMessage={t('modelNameRequired')}
            />
            <SelectFiled
              inputLabel={t('modelSeries')}
              options={deviceModelSeries}
              placeholder={t('selectModelSeries')}
              name="modelSeries"
              valueRequired
              selectedValue={deviceDetails.modelSeries}
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.modelSeries && formStatus.showFormErrors}
              errorMessage={t('modelSeriesRequired')}
            />
            <div className="switch-with-warning">
              <CustomInputSelectBox
                disabled={
                  deviceDetails.isInVLBC ||
                  (deviceDetails.isDeviceOffline && branding.ENERGIC === currentBranding)
                }
                inputLabel={t('maxPowerOutput')}
                inputType="number"
                placeholder={t('enterValue')}
                inputName="maximumOutputPower"
                selectName="maximumOutputPowerUnit"
                valueRequired
                inputValue={deviceDetails.maximumOutputPower}
                selectValue={deviceDetails.maximumOutputPowerUnit}
                selectOptions={maxPowerOutputUnits}
                handleValueChange={handleDeviceDetailChange}
                error={
                  !floatingNumberValidation(1, Infinity, deviceDetails.maximumOutputPower) &&
                  formStatus.showFormErrors
                }
                errorMessage={t('maxPowerOutputError')}
              />
              {warningMessage && <span className="warning-message">{warningMessage}</span>}
            </div>

            {deviceDetails.connectors.map((connector) => (
              <div key={connector.connectorId} className="full-width-field">
                <MultiSelectDropdown
                  placeholderFilled={`${t('connector')} ${connector.connectorId} ${t('type')}`}
                  title={`${t('selectConnector')} ${connector.connectorId} ${t('type')}`}
                  options={connectorTypes[currentBranding]}
                  name={`connectorType_${connector.connectorId}`}
                  placeholder={`${t('selectConnector')} ${connector.connectorId} ${t('type')}`}
                  search
                  selectedChips
                  selectedValues={connector.connectorType || []}
                  onSelectionChange={(_name, value) =>
                    handleConnectorTypeChange(connector.connectorId, value as string[])
                  }
                  error={!connector.connectorType.length && formStatus.showFormErrors}
                  errorMessage={t('connectorTypeRequired')}
                />
              </div>
            ))}

            <SelectFiled
              inputLabel={t('chargePointVendor')}
              options={chargePointVendors}
              placeholder={t('chargePointVendor')}
              name="chargePointVendor"
              valueRequired
              selectedValue={deviceDetails.chargePointVendor}
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.chargePointVendor && formStatus.showFormErrors}
              errorMessage={t('chargePointVendorError')}
            />
            <SelectFiled
              inputLabel={t('chargePointModel')}
              options={chargePointModels}
              placeholder={t('chargePointModel')}
              name="chargePointModel"
              valueRequired
              selectedValue={deviceDetails?.chargePointModel}
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.chargePointModel && formStatus.showFormErrors}
              errorMessage={t('chargePointModelError')}
            />

            <div className="switch-with-warning">
              <CustomInputSelectBox
                disabled={
                  deviceDetails.isInVLBC ||
                  (deviceDetails.isDeviceOffline && branding.ENERGIC === currentBranding)
                }
                inputLabel={t('desiredPowerOutput')}
                inputType="number"
                placeholder={t('enterValue')}
                inputName="desiredPowerOutput"
                selectName="powerUnit"
                inputValue={deviceDetails.desiredPowerOutput}
                handleValueChange={handleDeviceDetailChange}
                error={
                  !floatingNumberValidation(1, Infinity, deviceDetails.desiredPowerOutput) &&
                  formStatus.showFormErrors
                }
                errorMessage="Specify the Desired Power Output is required"
                selectValue={deviceDetails.powerUnit}
                selectOptions={desiredPowerOutputUnits}
              />
              {warningMessage && <span className="warning-message">{warningMessage}</span>}
            </div>

            <div className="switch-with-warning">
              <CustomInputSwitch
                disabled={
                  deviceDetails.isInVLBC ||
                  (deviceDetails.isDeviceOffline && branding.ENERGIC === currentBranding)
                }
                inputLabel={t('pncModeLabel')}
                value={deviceDetails.isJuiceBoxPncEnabled}
                name="isJuiceBoxPncEnabled"
                onChangeValue={handleDeviceDetailChange}
              />
              {warningMessage && <span className="warning-message">{warningMessage}</span>}
            </div>

            <CustomInputSwitch
              inputLabel={t('txDefaultProfile')}
              value={deviceDetails.useTxDefaultProfile}
              name="useTxDefaultProfile"
              onChangeValue={handleDeviceDetailChange}
            />
          </div>
        </div>
        <div className="add-device-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label={t('submit')}
              buttonType="submit"
              onClickFunc={handleSubmitDeviceDetails}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditDevice;
